import * as THREE from "three/build/three.module.js"
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js"
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js"
import { MeshoptDecoder } from "./meshopt_decoder.module.js"
import { CSS2DObject } from "three/examples/jsm/renderers/CSS2DRenderer.js"
import { STATE_FIPS_BY_ABBR } from "./data/FIPS.js"
import { Group as TweenGroup } from "./vendor/tweenjs/tween.esm.js"
import { MapRegion, Region } from "./Region.js"
import { injectFakeAO } from "./ShaderInjectors.js"

/**
 * @typedef {{signal?: AbortSignal}} AddCountiesOptions
 * @typedef {{signal?: AbortSignal}} AddIndividualCountyOption
 * @typedef {import("three/examples/jsm/loaders/GLTFLoader.js").GLTF} GLTF
 */

// Adds a label at the anchor point of mesh
function addLabel(mesh, text) {
    // Find the anchor
    const anchor = mesh.children.find(isAnchor)

    // Error check
    if (!anchor) {
        console.warn(
            "Tried to add label to " + mesh.name + " but no anchor found."
        )
        return
    }

    // Create the label
    const div = document.createElement("div")
    div.className = "label"
    div.textContent = text
    const label = new CSS2DObject(div)
    label.userData.labelType = "name"
    label.visible = true

    // Add the label as a child of anchor
    anchor.add(label)
}

function isBottomBorder(obj) {
    return obj.name.endsWith("_BottomBorder")
}

function isTopBorder(obj) {
    return obj.name.endsWith("_TopBorder")
}

function isAnchor(obj) {
    return (
        obj.name.split("_").at(-1).startsWith("Anchor") &&
        obj.children.length === 0
    )
}

function isTopLevel(obj, topLevelObj) {
    return obj.parent === topLevelObj
}

function isFace(obj, topLevel) {
    return isTopLevel(obj, topLevel) && !isTopBorder(obj)
}

function processCountyFace(mesh) {
    // mesh.region = new Region(mesh)
    // mesh.children.forEach((obj) => (obj.region = mesh.region))

    // Query county info
    const fips = mesh.name

    mesh.receiveShadow = true

    // Find name using anchor
    const name = mesh.children.find(isAnchor).name
    const readableName = name.substring(0, name.length - 7).replace(/_/g, " ")
    addLabel(mesh, readableName)

    // Save county info
    mesh.userData.name = readableName
    mesh.userData.fips = fips

    // Save the scale that the states will lower to while they're interactive
    mesh.userData.loweredScale = mesh.scale.clone()

    // Save the scale that the states will transition to when clicked
    mesh.userData.clickedScale = mesh.scale.clone().multiplyScalar(1.01)

    // Save the initial scale
    mesh.userData.initialScale = mesh.scale.clone()

    // Save the scale that states will be when lowered
    mesh.userData.hiddenScale = mesh.scale.clone()

    // Save the randomized scale that the states will raise to when first presented afte zooming in
    const scale = 1.045 + Math.random() * 0.025
    mesh.userData.randomScale = mesh.scale.clone().multiplyScalar(scale)

    mesh.userData.mapLevel = 0

    mesh.tweens = new TweenGroup()
}

function processCountyObject(
    obj,
    gltf,
    topBorderMaterial,
    bottomBorderMaterial
) {
    if (!obj.isMesh) return
    const mesh = obj

    /// //////////////////////////////////////////
    // Top-level mesh processing (face meshes)
    /// //////////////////////////////////////////
    if (isFace(mesh, gltf.scene)) {
        processCountyFace(mesh)

        /// ///////////////////////////////////////////
        // BottomBorder mesh processing
        /// ///////////////////////////////////////////
        mesh.children.filter(isBottomBorder).forEach((bottomBorder) => {
            bottomBorder.material = bottomBorderMaterial.clone()
            // Add radial gradient shader snippet to the bottom border material shader
            bottomBorder.material.onBeforeCompile = function (
                shader,
                renderer
            ) {
                injectFakeAO(shader, renderer)
            }
            bottomBorder.visible = false
            bottomBorder.castShadow = true
            bottomBorder.receiveShadow = true
        })

        /// ///////////////////////////////////////////
        // TopBorder mesh processing
        /// ///////////////////////////////////////////
        mesh.children.filter(isTopBorder).forEach((topBorder) => {
            topBorder.material = topBorderMaterial.clone()

            // Add radial gradient shader snippet to the top border material shader
            const uniforms = {
                color: { value: new THREE.Vector3(1, 1, 1) },
                center: { value: new THREE.Vector3(0, 50, 0) },
                attenuation: { value: 0.8 },
                strength: { value: 0.25 },
                startRadius: { value: 50.0 },
            }
            topBorder.material.onBeforeCompile = function (shader, renderer) {
                injectFakeAO(shader, renderer, uniforms)
            }
            topBorder.material.transparent = true
            // topBorder.material.opacity = 0.5
            topBorder.material.blending = THREE.CustomBlending
            topBorder.material.blendEquation = THREE.MaxEquation
            topBorder.castShadow = true
        })
    }

    /// ///////////////////////////////////////////
    // General mesh processing
    /// ///////////////////////////////////////////

    // Save the base color
    mesh.userData.baseColor = mesh.material.color.clone()

    if (mesh.material.emissive !== undefined) {
        mesh.userData.baseEmissive = mesh.material.emissive.clone()
    }

    // Save the color that the state should be when inactive (unhighlighted)
    mesh.userData.inactiveColor = mesh.material.color.clone()
    if (mesh.material.emissive !== undefined) {
        mesh.userData.inactiveEmissive = mesh.material.emissive.clone()
    }

    // Set the type of the mesh
    // The type of the mesh (state, county, zip, city) is determined by what file it comes from
    // Since that information isn't automatically attached to the mesh, we need to add it here
    mesh.userData.type = "county"
}

function disposeOfTopBorder(topBorder) {
    topBorder.geometry.dispose()
    topBorder.material.dispose()
    topBorder.parent.remove(topBorder)
}

/**
 * Recursively call .dispose() on every object that has a dispose method.
 * Leaves will be disposed first, then proceed up the tree.
 * note: if there is a loop in obj's property tree, this will hang forever
 * @param {GLTF | THREE.Object3D | {dispose?: () => void, forEach?: (fn: (ele: any) => void) => void} obj 
 * @param {Set} [objects]
 * @returns {void}
 */
function dispose(obj, objects) {
    // Create a new array to hold all the objects we're touching
    if (objects === undefined) {
        objects = new Set()
    }
    // Short circuit if we've already disposed this object (loop breaker)
    if (objects.has(obj)) {
        return
    }
    // Keep track that we've already disposed this object
    objects.add(obj)
    // Short circuit if we're not on an object
    if (typeof obj !== 'object' || obj === null) {
        return
    }
    if ('scene' in obj) {
        // obj is a gltf
        // Dispose every scene
        obj.scenes.forEach(scene => dispose(scene, objects))
    } else if ('children' in obj && 'forEach' in obj.children) {
        // obj is a THREE.Object3D
        // Dispose all children
        obj.children.forEach(child => dispose(child, objects))
    }

    // If object can be forEach'd, dispose all of them
    if ('forEach' in obj) {
        obj.forEach(ele => {
            dispose(ele, objects)
        })
    }

    // Dispose every single property of obj
    Object.keys(obj).forEach(key => {
        dispose(obj[key], objects)
    })

    // Finally, call .dispose() on obj
    if ('dispose' in obj) {
        console.log({obj}, 'disposing')
        obj.dispose()
    }
}

/**
 * @typedef {{signal?: AbortSignal}} LoadMapTextureOptions
 */
/**
 * 
 * @param {*} url 
 * @param {*} loader 
 * @param {LoadMapTextureOptions} options 
 * @returns 
 */
async function loadMapTexture(url, loader, options) {
    return new Promise((resolve, reject) => {
        loader.load(
            url,
            // success
            (texture) => {
                if (options?.signal?.aborted) {
                    dispose(texture)
                    reject(options.signal.reason)
                }
                texture.flipY = false
                texture.encoding = THREE.sRGBEncoding
                resolve(texture)
            },
            // progress
            null,
            // failure
            reject
        ) // End load
    }) // End new Promise()
}

/**
 * @typedef {{signal?: AbortSignal}} SetFaceMaterialTexturesOnLoadOptions
 */
/**
 * 
 * @param {Promise<THREE.Texture>} texturePromise 
 * @param {Promise<GLTF>} glbPromise 
 * @param {SetFaceMaterialTexturesOnLoadOptions} options 
 * @returns {Promise<void> | Promise<[THREE.MeshStandardMaterial, GLTF]>}
 */
async function setFaceMaterialTexturesOnLoad(texturePromise, glbPromise, options) {
    return Promise.all([texturePromise, glbPromise]).then(([texture, gltf]) => {
        if (options?.signal?.aborted) {
            console.warn('Aborted')
            throw new Error('Aborted')
        }
        // When both the texture and the model are loaded, connect them
        const material = new THREE.MeshStandardMaterial({
            map: texture,
            color: "#FFFFFF",
            emissive: "#000000",
        })
        gltf.scene.traverse((obj) => {
            if (!obj.isMesh) return

            const mesh = obj

            if (mesh.parent === gltf.scene && mesh.children.length) {
                mesh.material = material
            }
        }) // End traverse
        return [material, gltf]
    })
}

export class StateLoader {
    constructor(globe, textureInitializer) {
        this.scene = globe
        this.textureInitializer = textureInitializer
        this.textureLoader = new THREE.TextureLoader()
        this.loader = new GLTFLoader()
        this.loader.setMeshoptDecoder(MeshoptDecoder)
        const dracoLoader = new DRACOLoader()
        dracoLoader.setDecoderPath("/vendor/draco/")
        this.loader.setDRACOLoader(dracoLoader)
        this.shaderLoader = new THREE.FileLoader()
        THREE.Cache.enabled = true
    }

    /**
     *
     * @param {Number} fips - Fips/GEOID code of the county
     * @param {AddIndividualCountyOption} [options]
     * @returns {Promise<THREE.Mesh>} - face mesh of the region
     */
    async addIndividualCounty(fips, {signal} = {}) {
        // Initialize the materials we'll need

        const bottomBorderMaterial = new THREE.MeshLambertMaterial({
            side: THREE.DoubleSide,
            color: "#A0BDDA",
        })
        bottomBorderMaterial.onBeforeCompile = function (shader, renderer) {
            injectFakeAO(shader, renderer)
        }

        const topBorderMaterial = new THREE.MeshStandardMaterial()

        // Add radial gradient shader snippet to the top border material shader
        const uniforms = {
            color: { value: new THREE.Vector3(1, 1, 1) },
            center: { value: new THREE.Vector3(0, 50, 0) },
            attenuation: { value: 0.8 },
            strength: { value: 0.25 },
            startRadius: { value: 50.0 },
        }
        topBorderMaterial.onBeforeCompile = function (shader, renderer) {
            injectFakeAO(shader, renderer, uniforms)
        }
        topBorderMaterial.transparent = true
        topBorderMaterial.blending = THREE.CustomBlending
        topBorderMaterial.blendEquation = THREE.MaxEquation

        // Load geometry
        const gltf = await new Promise((resolve, reject) => {
            this.loader.load(
                "/assets/glb/globe/counties-individual/" + fips + ".glb",
                resolve,
                null,
                reject
            )
        })
        if (signal?.aborted === true) {
            console.warn(signal.reason)
            dispose(gltf)
            throw signal.reason
        }

        const region = new Region(gltf.scene)

        // TODO: When Region refactor is done, these back-references can be removed
        region.face.region = region
        region.face.userData.type = "individualCounty"

        region.bottomBorder.material = bottomBorderMaterial
        region.topBorder.material = topBorderMaterial

        // Scale up the whole region to raise it
        region.face.scale.multiplyScalar(52.2)

        region.bottomBorder.castShadow = true
        region.bottomBorder.receiveShadow = true

        // TODO: Region refactor, return region instead of region.face
        return region.face
    }

    /**
     * @example
     * abort = new AbortController()
     * addCounties('MO', {signal: abort.signal})
     * @param {string} state state abbreviation
     * @param {AddCountiesOptions} [options]
     * @returns {Promise<GLTF>}
     */
    addCounties(state, options) {
        const scope = this

        const fips = STATE_FIPS_BY_ABBR[state].fips

        const countiesGLBPromise = new Promise((resolve, reject) => {
            this.loader.load(
                "/assets/glb/globe/counties/" + fips + ".glb",
                // onLoad
                function (gltf) {
                    if (options?.signal?.aborted) {
                        console.warn(options.signal.reason)
                        dispose(gltf)
                        throw options.signal.reason
                    }
                    gltf.scene.userData.mapLevel = 0

                    // Initialize the materials we'll need
                    const bottomBorderMaterial = new THREE.MeshLambertMaterial({
                        side: THREE.DoubleSide,
                        color: "#A0BDDA",
                    })

                    const topBorderMaterial = new THREE.MeshBasicMaterial()

                    gltf.scene.traverse((obj) =>
                        processCountyObject(
                            obj,
                            gltf,
                            topBorderMaterial,
                            bottomBorderMaterial
                        )
                    )

                    // Remove conjoined top border
                    disposeOfTopBorder(gltf.scene.children.find(isTopBorder))

                    gltf.scene.scale.multiplyScalar(48.2)
                    resolve(gltf)
                },
                // onProgress - no Promise equivalent
                null,
                // onError
                function (error) {
                    reject(error)
                }
            ) // End loader.load()
        }) // End new Promise()

        const mergedBorderGLBPromise = new Promise((resolve, reject) => {
            this.loader.load(
                "/assets/glb/globe/counties-group-border/" + fips + ".glb",
                // onLoad
                function (gltf) {
                    if (options?.signal?.aborted) {
                        console.warn(options.signal.reason)
                        dispose(gltf)
                        throw options.signal.reason
                    }

                    // Initialize the materials we'll need
                    const bottomBorderMaterial = new THREE.MeshLambertMaterial({
                        side: THREE.DoubleSide,
                        color: "#A0BDDA",
                    })

                    bottomBorderMaterial.onBeforeCompile = function (
                        shader,
                        renderer
                    ) {
                        injectFakeAO(shader, renderer)
                    }


                    gltf.scene.traverse(obj => {
                        if (!obj.isMesh) return
                        obj.material = bottomBorderMaterial
                        obj.castShadow = true
                    })

                    // gltf.scene.scale.multiplyScalar(48.2)
                    resolve(gltf)
                },
                // onProgress - no Promise equivalent
                null,
                // onError
                function (error) {
                    reject(error)
                }
            ) // End loader.load()
        }) // End new Promise()

        // Start loading map texture
        const url = "/assets/glb/image/county-groups/1024/" + fips + ".webp"
        const texturePromise = loadMapTexture(url, scope.textureLoader, {signal: options.signal})

        // Put merged border as child of counties group
        Promise.all([countiesGLBPromise, mergedBorderGLBPromise]).then(([counties, mergedBorder]) => {
            counties.scene.add(mergedBorder.scene)
        })

        // TODO: Add signal
        return setFaceMaterialTexturesOnLoad(texturePromise, countiesGLBPromise).then(
            ([material, gltf]) => {
                if (options?.signal?.aborted) {
                    console.warn('Aborted')
                    throw new Error('Aborted')
                }
                // Make mesh faces transparent
                // const mat = new THREE.MeshStandardMaterial({
                //     transparent: true,
                //     opacity: 0,
                //     blending: THREE.CustomBlending,
                //     blendEquation: THREE.AddEquation,
                //     blendSrc: THREE.OneFactor,
                //     blendDst: THREE.ZeroFactor,
                // })

                gltf.scene.userData.materials = {}
                gltf.scene.userData.materials.active = []
                gltf.scene.userData.materials.inactive = []
                gltf.scene.userData.materials.holePunch = []

                // Hole punch material
                // const holePunch = material.clone()
                // holePunch.transparent = false
                // holePunch.opacity = 0
                // holePunch.blending = THREE.CustomBlending
                // holePunch.blendEquation = THREE.AddEquation
                // holePunch.blendSrc = THREE.OneFactor
                // holePunch.blendDst = THREE.ZeroFactor
                const holePunch = new THREE.MeshLambertMaterial({
                    transparent: false,
                    opacity: 0,
                    blending: THREE.CustomBlending,
                    blendEquation: THREE.AddEquation,
                    blendSrc: THREE.OneFactor,
                    blendDst: THREE.ZeroFactor,
                    color: "#000",
                    emissive: "#000",
                })

                // Materials for mapLevel=0
                gltf.scene.userData.materials.active.push(material)
                gltf.scene.userData.materials.inactive.push(material.clone())
                gltf.scene.userData.materials.holePunch.push(holePunch)

                // Materials for mapLevel=1
                gltf.scene.userData.materials.active.push(material.clone())
                gltf.scene.userData.materials.inactive.push(material.clone())
                gltf.scene.userData.materials.holePunch.push(holePunch.clone())

                gltf.scene.children.forEach((child) => {
                    if (!isFace(child, gltf.scene)) return
                    child.region = new MapRegion(child, {
                        active: gltf.scene.userData.materials.active,
                        inactive: gltf.scene.userData.materials.inactive,
                        holePunch: gltf.scene.userData.materials.holePunch,
                    })
                })

                // Start loading higher resolution map texture
                this.textureLoader.load(
                    "/assets/glb/image/county-groups/4096/" + fips + ".webp",
                    // success
                    (texture) => {
                        if (options?.signal?.aborted) {
                            console.warn('Aborted')
                            dispose(texture)
                            return
                        }
                        texture.flipY = false
                        texture.encoding = THREE.sRGBEncoding

                        gltf.scene.userData.materials.active[1].map = texture
                        gltf.scene.userData.materials.inactive[1].map = texture
                        // gltf.scene.userData.materials.holePunch[1].map = texture

                        scope.textureInitializer.init(texture)
                    },
                    null,
                    (err) => {
                        console.error(err)
                    }
                )

                return gltf
            }
        ) // End Promise.all
    } // End addCounties()

    async addZip(fips) {
        const glbPromise = new Promise((resolve, reject) => {
            this.loader.load(
                "/assets/glb/globe/zips/" + fips + ".glb",
                // onLoad
                function (gltf) {
                    // Initialize the materials we'll need
                    const bottomBorderMaterial = new THREE.MeshLambertMaterial({
                        side: THREE.DoubleSide,
                        color: "#A0BDDA",
                    })

                    // Add radial gradient shader snippet to the bottom border material shader
                    bottomBorderMaterial.onBeforeCompile = function (
                        shader,
                        renderer
                    ) {
                        injectFakeAO(shader, renderer)
                    }

                    const topBorderMaterial = new THREE.MeshStandardMaterial()

                    // Add radial gradient shader snippet to the top border material shader
                    const uniforms = {
                        color: { value: new THREE.Vector3(1, 1, 1) },
                        center: { value: new THREE.Vector3(0, 50, 0) },
                        attenuation: { value: 0.8 },
                        strength: { value: 0.25 },
                        startRadius: { value: 50.0 },
                    }
                    topBorderMaterial.onBeforeCompile = function (
                        shader,
                        renderer
                    ) {
                        injectFakeAO(shader, renderer, uniforms)
                    }
                    topBorderMaterial.transparent = true
                    topBorderMaterial.blending = THREE.CustomBlending
                    topBorderMaterial.blendEquation = THREE.MaxEquation

                    // Remove non-conjoined top borders
                    const topBorders = gltf.scene.children
                        .map((topLevel) => topLevel.children)
                        .flat(1)
                        .filter(isTopBorder)

                    topBorders.forEach((topBorder) => {
                        disposeOfTopBorder(topBorder)
                    })

                    gltf.scene.traverse((obj) => {
                        if (!obj.isMesh) return

                        const mesh = obj

                        /// //////////////////////////////////////////
                        // Top-level mesh processing (face meshes)
                        /// //////////////////////////////////////////
                        if (mesh.parent === gltf.scene) {
                            // Region is part of the upcoming refactor
                            mesh.region = new Region(mesh)
                            mesh.children.forEach(
                                (obj) => (obj.region = mesh.region)
                            )

                            const readableName = mesh.name.replace(/_/g, " ")
                            addLabel(mesh, readableName)

                            // Save the scale that the states will lower to while they're interactive
                            mesh.userData.loweredScale = mesh.scale.clone()

                            // Save the scale that the states will transition to when clicked
                            mesh.userData.clickedScale = mesh.scale.clone()

                            // Save the initial scale
                            mesh.userData.initialScale = mesh.scale.clone()

                            // Save the scale that states will be when lowered
                            mesh.userData.hiddenScale = mesh.scale.clone()

                            // Save the randomized scale that the states will raise to when first presented afte zooming in
                            const scale = 1.015 + Math.random() * 0.025
                            mesh.userData.randomScale = mesh.scale
                                .clone()
                                .multiplyScalar(scale)

                            mesh.tweens = new TweenGroup()
                        }

                        /// ///////////////////////////////////////////
                        // BottomBorder mesh processing
                        /// ///////////////////////////////////////////
                        if (isBottomBorder(mesh)) {
                            mesh.material = bottomBorderMaterial
                            mesh.castShadow = true
                            mesh.receiveShadow = true
                        }

                        /// ///////////////////////////////////////////
                        // TopBorder mesh processing
                        /// ///////////////////////////////////////////
                        if (isTopBorder(mesh)) {
                            mesh.material = topBorderMaterial
                            mesh.castShadow = true
                        }

                        /// ///////////////////////////////////////////
                        // General mesh processing
                        /// ///////////////////////////////////////////

                        // Save the base color
                        mesh.userData.baseColor = mesh.material.color.clone()
                        mesh.userData.baseEmissive =
                            mesh.material.emissive.clone()

                        // Save the color that the state should be when inactive (unhighlighted)
                        mesh.userData.inactiveColor =
                            mesh.material.color.clone()
                        mesh.userData.inactiveEmissive =
                            mesh.material.emissive.clone()

                        // Set the type of the mesh
                        // The type of the mesh (state, county, zip, city) is determined by what file it comes from
                        // Since that information isn't automatically attached to the mesh, we need to add it here
                        mesh.userData.type = "zip"
                    })

                    gltf.scene.scale.multiplyScalar(52.2)
                    // gltf.scene.rotation.x = -Math.PI * 0.003
                    // gltf.scene.rotation.y = Math.PI * 0.006
                    // gltf.scene.rotation.z = -Math.PI * 0.007

                    resolve(gltf)
                },
                // onProgress
                null,
                // onError
                function (error) {
                    reject(error)
                }
            )
        }) // end new Promise()

        const url = "/assets/glb/image/zip-groups/" + fips + ".webp"
        const texturePromise = loadMapTexture(url, this.textureLoader)

        return setFaceMaterialTexturesOnLoad(texturePromise, glbPromise).then(
            ([material, gltf]) => {
                return gltf
            }
        )
    } // end addZip()
}
